import axios from "axios";

// Action Creators
export const salesLoginStart = () => ({
  type: "SALES_LOGIN_START",
});

export const salesLoginSuccess = (user) => ({
  type: "SALES_LOGIN_SUCCESS",
  payload: user,
});

export const salesLoginFailure = (error) => ({
  type: "SALES_LOGIN_FAILURE",
  payload: error,
});

export const salesRegisterSuccess = (user) => ({
  type: "SALES_REGISTER_SUCCESS",
  payload: user,
});

export const salesVerifySuccess = (user) => ({
  type: "SALES_VERIFY_SUCCESS",
  payload: user,
});

export const salesVerifyFailure = (error) => ({
  type: "SALES_VERIFY_FAILURE",
  payload: error,
});

export const salesLogout = () => ({
  type: "SALES_LOGOUT",
});

export const salesUpdateUser = (userData) => ({
  type: "SALES_UPDATE_USER",
  payload: userData,
});

export const salesClearError = () => ({
  type: "SALES_CLEAR_ERROR",
});

// Async Actions
export const validateSalesRegistration = (email, token) => async () => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_BASE_URL}/nocApi/sales/validate-registration`,
      {
        email,
        token,
      }
    );

    if (res?.data?.success) {
      return true;
    } else {
      return false;
    }
  } catch (error) {
    console.error("Validation error:", error);
    return false;
  }
};

// Async Actions
export const salesRegister =
  (email, password, confirmPassword, token) => async (dispatch) => {
    try {
      dispatch(salesLoginStart());

      const res = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/sales/register`,
        {
          email,
          password,
          confirmPassword,
          token,
        },
        { withCredentials: true }
      );

      if (res.data.success) {
        // Store temp token in sessionStorage for hard refresh recovery
        sessionStorage.setItem(
          "salesUser",
          JSON.stringify({
            temp_token: res.data.data.temp_token,
            timestamp: Date.now(),
          })
        );

        dispatch(salesRegisterSuccess(res.data.data));
        return {
          success: true,
          data: res.data.data,
        };
      } else {
        dispatch(salesLoginFailure(res.data.message || "Registration failed"));
        return {
          success: false,
          message: res.data.message || "Registration failed",
        };
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        "An error occurred during registration";
      dispatch(salesLoginFailure(errorMessage));
      return {
        success: false,
        message: errorMessage,
      };
    }
  };

export const salesLogin = (email, password) => async (dispatch) => {
  try {
    dispatch(salesLoginStart());

    const res = await axios.post(
      `${process.env.REACT_APP_BASE_URL}/nocApi/sales/login`,
      {
        email,
        password,
      },
      { withCredentials: true }
    );

    if (res.data.success) {
      // Store temp token in sessionStorage for hard refresh recovery
      sessionStorage.setItem(
        "salesUser",
        JSON.stringify({
          temp_token: res.data.data.temp_token,
          timestamp: Date.now(),
        })
      );

      dispatch(salesLoginSuccess(res.data.data));
      return {
        success: true,
        data: res.data.data,
      };
    } else {
      dispatch(salesLoginFailure(res.data.message || "Login failed"));
      return {
        success: false,
        message: res.data.message || "Login failed",
      };
    }
  } catch (error) {
    const errorMessage = error.response?.data?.message || "Invalid credentials";
    dispatch(salesLoginFailure(errorMessage));
    return {
      success: false,
      message: errorMessage,
    };
  }
};

export const salesVerifySession = () => async (dispatch) => {
  try {
    dispatch(salesLoginStart());

    // Check sessionStorage first
    const storedUser = sessionStorage.getItem("salesUser");
    if (!storedUser) {
      dispatch(salesVerifyFailure("No session found"));
      return { success: false, message: "No session found" };
    }

    const { temp_token } = JSON.parse(storedUser);
    if (!temp_token) {
      sessionStorage.removeItem("salesUser");
      dispatch(salesVerifyFailure("No temp token found"));
      return { success: false, message: "No temp token found" };
    }

    // Verify with backend
    const res = await axios.post(
      `${process.env.REACT_APP_BASE_URL}/nocApi/sales/verify`,
      { temp_token },
      { withCredentials: true }
    );

    if (res.data.success) {
      // Update sessionStorage with fresh timestamp after successful verification
      sessionStorage.setItem(
        "salesUser",
        JSON.stringify({
          temp_token: temp_token,
          timestamp: Date.now(),
        })
      );

      dispatch(salesVerifySuccess(res.data.data));
      return {
        success: true,
        data: res.data.data,
      };
    } else {
      // Clear invalid session
      sessionStorage.removeItem("salesUser");
      dispatch(salesVerifyFailure(res.data.message || "Session expired"));
      return {
        success: false,
        message: res.data.message || "Session expired",
      };
    }
  } catch (error) {
    // Clear invalid session on error
    sessionStorage.removeItem("salesUser");
    const errorMessage =
      error.response?.data?.message || "Session verification failed";
    dispatch(salesVerifyFailure(errorMessage));
    return {
      success: false,
      message: errorMessage,
    };
  }
};

export const salesLogoutUser = () => async (dispatch) => {
  try {
    // Call backend logout if needed
    await axios.post(
      `${process.env.REACT_APP_BASE_URL}/nocApi/sales/logout`,
      {},
      { withCredentials: true }
    );
  } catch (error) {
    // Continue with logout even if backend call fails
    console.error("Logout error:", error);
  } finally {
    // Clear sessionStorage
    sessionStorage.removeItem("salesUser");
    // Dispatch logout action
    dispatch(salesLogout());
  }
};
